import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  ShieldCheck,
  Trash2,
  Loader2,
  Crown,
  User,
  Users,
  UserX,
  Search,
} from "lucide-react";
import { Input } from "../components/ui/input";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const RoleBadge = ({ role }) => {
  const styles = {
    admin: "bg-violet-100 text-violet-700 border-violet-200",
    owner: "bg-amber-100 text-amber-700 border-amber-200",
    team_member: "bg-sky-100 text-sky-700 border-sky-200",
  };

  return (
    <span
      className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full border ${styles[role] || "bg-muted text-muted-foreground"}`}
      data-testid={`role-badge-${role}`}
    >
      {role === "admin" && <ShieldCheck className="w-3 h-3" />}
      {role === "owner" && <Crown className="w-3 h-3" />}
      {role === "team_member" && <Users className="w-3 h-3" />}
      {role.replace("_", " ")}
    </span>
  );
};

const AdminTab = () => {
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/users`, {
        withCredentials: true,
      });
      setUsers(res.data.users || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to load users");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    setDeleting(true);
    try {
      const res = await axios.delete(
        `${BACKEND_URL}/api/admin/users/${deleteConfirm.user_id}`,
        { withCredentials: true }
      );
      toast.success(res.data.message || "User deleted");
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete user");
    } finally {
      setDeleting(false);
      setDeleteConfirm(null);
    }
  };

  const filtered = users.filter((u) => {
    const q = search.toLowerCase();
    return (
      !q ||
      (u.name || "").toLowerCase().includes(q) ||
      (u.email || "").toLowerCase().includes(q) ||
      (u.role || "").toLowerCase().includes(q)
    );
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="admin-loading">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-tab">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5" />
            User Management
          </CardTitle>
          <CardDescription>
            {users.length} registered {users.length === 1 ? "user" : "users"} in the system
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Search */}
          <div className="relative max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search by name, email, or role..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
              data-testid="admin-search-input"
            />
          </div>

          {/* User list */}
          <div className="divide-y rounded-lg border">
            {filtered.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-muted-foreground" data-testid="no-users-found">
                <UserX className="w-10 h-10 mb-2 opacity-40" />
                <p className="text-sm">No users found</p>
              </div>
            ) : (
              filtered.map((u) => (
                <div
                  key={u.user_id}
                  className="flex items-center justify-between px-4 py-3 hover:bg-muted/30 transition-colors"
                  data-testid={`user-row-${u.user_id}`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    {u.picture ? (
                      <img
                        src={u.picture}
                        alt={u.name}
                        className="w-9 h-9 rounded-full shrink-0"
                      />
                    ) : (
                      <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                        <User className="w-4 h-4 text-primary" />
                      </div>
                    )}
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium truncate">{u.name || "—"}</span>
                        <RoleBadge role={u.role} />
                      </div>
                      <span className="text-sm text-muted-foreground truncate block">
                        {u.email}
                      </span>
                    </div>
                  </div>

                  {u.role !== "admin" && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setDeleteConfirm(u)}
                      className="text-muted-foreground hover:text-destructive shrink-0 ml-2"
                      data-testid={`delete-user-${u.user_id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete User?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete{" "}
              <strong>{deleteConfirm?.name || deleteConfirm?.email}</strong>?
              {!deleteConfirm?.organization_id && (
                <span className="block mt-2 text-destructive font-medium">
                  This user is an organization owner. Deleting them will also
                  remove all their team members and associated data.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="confirm-delete-user"
            >
              {deleting ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : null}
              Delete User
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminTab;
